import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, Router, RouterStateSnapshot } from '@angular/router';
import { ContactApiService } from '@aiwyn/api';
import { ROUTE_TOKEN_LOGIN } from '../routing-constants';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { ModalAlertComponent } from '../components/main/modals/alert/modal-alert.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@aiwyn/auth';
import { Logger } from '@aiwyn/shared';

const QUERY_PARAM_ENCRYPTED_EMAIL = 'data';

@Injectable({
  providedIn: 'root'
})
export class VerifyEncryptedEmailGuardService implements CanActivate{

  constructor(
    private readonly contactApiService: ContactApiService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService,
    private readonly logger: Logger,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): MaybeAsync<GuardResult> {
    this.logger.info('VerifyEncryptedEmailGuardService :: canActivate :: starting the email verification process');

    const encryptedEmail = route.queryParams[QUERY_PARAM_ENCRYPTED_EMAIL];
    if (!encryptedEmail) {
      this.logger.info('VerifyEncryptedEmailGuardService :: canActivate :: User attempted to navigate to the verify page without an email');
      return this.router.createUrlTree([ROUTE_TOKEN_LOGIN]);
    }

    return this.contactApiService.verifyContactEmail(encryptedEmail).pipe(
      take(1),
      switchMap(_ => {
        this.logger.info('AUTH :: VerifyEncryptedEmailGuardService :: canActivate :: Successfully decrypted email, redirecting to login');
        return this.authService.refreshToken().pipe(
          switchMap(_ => {
            return this.dialog.open(
              ModalAlertComponent,
              {
                data: {
                  title: 'Registration Successful!',
                  message: 'You can use your credentials to log in to your client portal.',
                },
              },
            ).afterClosed();
          }),
        );
      }),
      catchError(error => {
        this.logger.error(
          'AUTH :: VerifyEncryptedEmailGuardService :: canActivate :: failed to decrypt email for verification! redirecting to login',
          error
        );

        return this.dialog.open(
          ModalAlertComponent,
          {
            data: {
              title: 'Login Error',
              message: 'We were unable to locate your contact record. Please contact your firm finance contact for assistance.',
            }
          }
        ).afterClosed();
      }),
      // Sign out for a clean slate
      switchMap(_ => this.authService.signOut(false)),
      tap(_ => this.authService.isReadyToAuthenticate.set(true)),
      // Pitch them over to the login
      map(_ => this.router.createUrlTree([ROUTE_TOKEN_LOGIN])),
      take(1),
    );
  }
}
